import { SITE_URL } from "./constants";

export const generateOrganizationSchema = () => ({
    // "@context": "https://schema.org",
    "@type": "Organization",
    name: "UpNComer",
    url: SITE_URL,
    description:
        "UpNComer is an AI-powered operating system for independent artists.",
    sameAs: [
        "https://www.instagram.com/upncomer.co/",
    ],
});

export const generateWebsiteSchema = () => ({
    // "@context": "https://schema.org",
    "@type": "WebSite",
    name: "UpNComer",
    url: SITE_URL,
    description:
        "The Operating System for Independent Music. Built by artists who understand the struggle. Get the power of a full label team while keeping 100% creative and financial control.",
});

export const generateSoftwareApplicationSchema = () => ({
    // "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "UpNComer",
    url: SITE_URL,
    applicationCategory: "MusicApplication",
    operatingSystem: "Web",
    description:
        "AI-powered platform for independent musicians.",
    featureList: [
        "AI music marketing",
        "Music distribution",
        "Artist analytics",
        "Fan engagement",
        "AI mastering",
        "Playlist pitching",
        "Press outreach",
        "Content creation",
        "Artist education",
        "Release planning",
        "Music career management",
    ],
    keywords: [
        "independent artists",
        "music distribution",
        "music marketing",
        "artist development",
        "playlist pitching",
        "AI mastering",
        "fan engagement",
        "music monetization",
        "artist growth",
        "streaming analytics"
    ],
    audience: {
        "@type": "Audience",
        audienceType:
            "Independent artists, musicians, DIY artists",
    },
});

export const generateModuleSchema = () => ({
    // "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "UpNComer Platform Modules",
    "description": "Core modules within the UpNComer AI operating system for independent artists.",
    "hasDefinedTerm": [
        {
            "@type": "DefinedTerm",
            "name": "Amplitude AI",
            "description": "AI-powered music marketing, audience intelligence, and artist growth tools for independent artists."
        },
        {
            "@type": "DefinedTerm",
            "name": "Growth Engine",
            "description": "Artist growth planning, release strategy, fan engagement, and career development module."
        },
        {
            "@type": "DefinedTerm",
            "name": "Artist Manager",
            "description": "Artist management platform for tracking releases, audience growth, streaming performance, and career milestones."
        },
        {
            "@type": "DefinedTerm",
            "name": "Data Analyst",
            "description": "Music analytics and performance intelligence module providing actionable insights from streaming and audience data."
        },
        {
            "@type": "DefinedTerm",
            "name": "UrStudio",
            "description": "Music production, mixing, mastering, and studio services platform for independent musicians."
        },
        {
            "@type": "DefinedTerm",
            "name": "Publicist",
            "description": "Press outreach, music promotion, media coverage, and public relations module for artists."
        },
        {
            "@type": "DefinedTerm",
            "name": "Content Creator",
            "description": "Content creation tools for music marketing, social media campaigns, artist branding, and fan engagement."
        },
        {
            "@type": "DefinedTerm",
            "name": "ProfessorU",
            "description": "Educational platform providing artist development, music business education, and industry training resources."
        },
        {
            "@type": "DefinedTerm",
            "name": "Distribution",
            "description": "Music distribution platform delivering releases to streaming services and digital music stores worldwide."
        }
    ]
})

export const generateKnowledgeGraphSchema = () => ({
    // "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "UpNComer Music Growth Knowledge Base",
    "description": "Core concepts, strategies, and topics used by independent artists to build sustainable music careers.",
    "hasDefinedTerm": [
        {
            "@type": "DefinedTerm",
            "name": "Independent Artists",
            "description": "Musicians and creators who manage and grow their careers without traditional major label representation."
        },
        {
            "@type": "DefinedTerm",
            "name": "Music Marketing",
            "description": "Strategies and campaigns used to promote music, attract listeners, and grow audiences."
        },
        {
            "@type": "DefinedTerm",
            "name": "Music Distribution",
            "description": "The process of delivering music releases to streaming platforms and digital stores."
        },
        {
            "@type": "DefinedTerm",
            "name": "Artist Development",
            "description": "The process of improving an artist's skills, branding, audience growth, and career strategy."
        },
        {
            "@type": "DefinedTerm",
            "name": "Fan Engagement",
            "description": "Methods used by artists to build stronger relationships and interactions with fans."
        },
        {
            "@type": "DefinedTerm",
            "name": "AI Music Tools",
            "description": "Artificial intelligence-powered tools that help artists with marketing, production, analytics, and growth."
        },
        {
            "@type": "DefinedTerm",
            "name": "Playlist Pitching",
            "description": "The process of submitting music to playlist curators to increase visibility and streaming performance."
        },
        {
            "@type": "DefinedTerm",
            "name": "Music Analytics",
            "description": "The measurement and analysis of streaming performance, audience behavior, and artist growth metrics."
        },
        {
            "@type": "DefinedTerm",
            "name": "Release Strategy",
            "description": "The planning and execution process for launching music releases and maximizing audience reach."
        },
        {
            "@type": "DefinedTerm",
            "name": "AI Mastering",
            "description": "The use of artificial intelligence to master music tracks for professional sound quality."
        },
        {
            "@type": "DefinedTerm",
            "name": "Artist Growth",
            "description": "The ongoing process of increasing audience size, engagement, visibility, and career opportunities."
        },
        {
            "@type": "DefinedTerm",
            "name": "Music Monetization",
            "description": "Methods used by artists to generate revenue through streaming, merchandise, licensing, memberships, and fan support."
        }
    ]
})

export const generateWebPageSchema = ({
    title,
    description,
    url,
}) => ({
    // "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
});

export const generateServiceSchema = ({
    name,
    description,
    url,
}) => ({
    // "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: {
        "@type": "Organization",
        name: "UpNComer",
    },
    areaServed: "Worldwide",
    serviceType: name,
    url,
});

export const generateProductSchema = ({
    name,
    description,
    price,
    currency = "USD",
    url,
}) => ({
    // "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    offers: {
        "@type": "Offer",
        price,
        priceCurrency: currency,
        availability:
            "https://schema.org/InStock",
        url,
    },
});

export const generateFAQSchema = (faqs = []) => ({
    // "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
        },
    })),
});

export const generateFAQPageSchema = () => ({
    //   "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "What is UpNComer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "UpNComer is an AI-powered operating system for independent artists that combines music distribution, music marketing, artist development, music analytics, fan engagement, release strategy, AI mastering, and music monetization tools into one platform."
            }
        },
        {
            "@type": "Question",
            "name": "How does UpNComer help independent artists grow their careers?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "UpNComer helps independent artists grow through AI-powered music marketing, audience development, fan engagement, release planning, distribution, analytics, content creation, education, and career management tools."
            }
        },
        {
            "@type": "Question",
            "name": "What music marketing tools are available in UpNComer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "UpNComer provides AI-powered music marketing tools including Amplitude AI, Content Creator, Publicist, audience growth solutions, campaign planning, release promotion, and artist branding resources."
            }
        },
        {
            "@type": "Question",
            "name": "How does UpNComer support music distribution?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Distribution module helps independent artists distribute music to major streaming platforms and digital music stores while supporting release strategy, audience growth, and music monetization goals."
            }
        },
        {
            "@type": "Question",
            "name": "What is Amplitude AI?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Amplitude AI is UpNComer's AI-powered music marketing and artist growth module that helps musicians understand audiences, improve fan engagement, optimize campaigns, and grow their careers."
            }
        },
        {
            "@type": "Question",
            "name": "What is Growth Engine?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Growth Engine is UpNComer's strategic planning module designed to help independent artists develop release strategies, audience growth plans, marketing campaigns, and long-term career roadmaps."
            }
        },
        {
            "@type": "Question",
            "name": "What does Artist Manager do?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Artist Manager helps musicians manage releases, track audience growth, monitor streaming performance, organize projects, and coordinate career development activities."
            }
        },
        {
            "@type": "Question",
            "name": "How does Data Analyst help musicians?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Data Analyst provides music analytics and performance insights that help artists understand listener behavior, streaming trends, audience engagement, and growth opportunities."
            }
        },
        {
            "@type": "Question",
            "name": "What is playlist pitching and why is it important?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Playlist pitching is the process of submitting music to playlist curators to increase visibility, streams, audience discovery, and overall artist growth. It is an important part of modern music marketing and release strategy."
            }
        },
        {
            "@type": "Question",
            "name": "How does UpNComer help with fan engagement?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "UpNComer helps artists strengthen fan engagement through audience insights, content creation tools, personalized marketing strategies, community-building resources, and growth-focused campaigns."
            }
        },
        {
            "@type": "Question",
            "name": "What educational resources are available on UpNComer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "ProfessorU provides artist development resources, music business education, marketing guidance, release planning tutorials, and practical training designed for independent artists."
            }
        },
        {
            "@type": "Question",
            "name": "What is AI mastering?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "AI mastering uses artificial intelligence technology to enhance and optimize music tracks for professional sound quality. UpNComer's UrStudio module supports production, mixing, mastering, and studio-related workflows."
            }
        },
        {
            "@type": "Question",
            "name": "How can artists monetize their music with UpNComer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "UpNComer supports music monetization through distribution, audience growth, fan engagement strategies, marketing optimization, release planning, and data-driven career development."
            }
        }
    ]
})

export const generateBreadcrumbSchema = (items = []) => {
    return {
        // "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: `${SITE_URL}${item.path}`,
        })),
    };
};

export const generateGraphSchema = (schemas = []) => {
    const flat = schemas
        .flat()
        .filter(Boolean)
        .flatMap((schema) => {
            // If a schema is already a @graph wrapper, pull its contents out
            // so we don't end up with a nested @graph inside @graph.
            if (schema["@graph"] && Array.isArray(schema["@graph"])) {
                return schema["@graph"];
            }
            return schema;
        });
    return {
        "@context": "https://schema.org",
        "@graph": flat,
    };
};